import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Search, Users, HeartPulse, Mail, MapPin, ShieldCheck, ArrowUp, ArrowDown, ChevronsUpDown, Eye, EyeOff, MailX, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { useUiState } from "@/lib/uiState";

const ago = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), now = new Date(), s = Math.floor((now - d) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 172800) return "yesterday";
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
};

export default function ClassDetail() {
  const { year } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(() => { try { return JSON.parse(localStorage.getItem("ud_classdetail_sort")) || { key: "name", dir: "asc" }; } catch { return { key: "name", dir: "asc" }; } });
  const [hideDeceased, setHideDeceased] = useState(() => localStorage.getItem("ud_classdetail_hide_deceased") === "true");
  const [hideOptedOut, setHideOptedOut] = useState(() => localStorage.getItem("ud_classdetail_hide_optout") === "true");
  const [recentOpen, setRecentOpen] = useUiState("ud_classdetail_recent_open", true);

  useEffect(() => {
    api.get(`/classes/${year}`).then((r) => setData(r.data));
  }, [year]);

  const toggleAgent = async (a, e) => {
    e.stopPropagation();
    const enable = !a.is_class_agent;
    const msg = enable
      ? (a.claimed
          ? `Make ${a.name.trim()} a Class Agent for the Class of ${year}?`
          : `${a.name.trim()} hasn't claimed their profile yet. Make them a Class Agent now? Their agent access will activate automatically when they claim their profile.`)
      : `Remove Class Agent access for ${a.name.trim()}?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/alumni/${a.id}/class-agent`, { enabled: enable });
      toast.success(enable ? (data?.pending_claim ? "Class Agent designated — activates when they claim their profile" : "Class Agent designated") : "Class Agent removed");
      const { data: fresh } = await api.get(`/classes/${year}`);
      setData(fresh);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update Class Agent status"); }
  };

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  const sortValue = (r, key) => {
    if (key === "city") return (r.city || "").toLowerCase();
    if (key === "state") return (r.state || "").toLowerCase();
    return (r.last_name || (r.name || "").trim().split(" ").slice(-1)[0] || "").toLowerCase();
  };
  const toggleSort = (key) => setSort((prev) => {
    const next = prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" };
    localStorage.setItem("ud_classdetail_sort", JSON.stringify(next));
    return next;
  });
  const sortIcon = (k) => (sort.key === k
    ? (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
    : <ChevronsUpDown className="h-3 w-3 opacity-40" />);
  const roster = data.roster
    .filter((r) => r.name.toLowerCase().includes(q.toLowerCase()) || (r.email || "").toLowerCase().includes(q.toLowerCase()))
    .filter((r) => (hideDeceased ? r.living_status !== "deceased" : true))
    .filter((r) => (hideOptedOut ? !r.opted_out : true))
    .slice()
    .sort((a, b) => {
      const va = sortValue(a, sort.key), vb = sortValue(b, sort.key);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  const s = data.stats;
  const stats = [
    { icon: Users, label: "Total Alumni", value: s.total, accent: "bg-primary/10 text-primary" },
    { icon: HeartPulse, label: "Living", value: s.living, accent: "bg-emerald-100 text-emerald-700" },
    { icon: Mail, label: "With Email", value: s.with_email, accent: "bg-blue-100 text-blue-700" },
    { icon: MapPin, label: "With Address", value: s.with_address, accent: "bg-amber-100 text-amber-700" },
    { icon: MailX, label: "Opted Out", value: s.opted_out || 0, accent: "bg-rose-100 text-rose-700" },
  ];

  return (
    <div className="fade-up">
      <button onClick={() => navigate("/classes")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-2">
        <ArrowLeft className="h-4 w-4" /> All Classes
      </button>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="font-serif text-2xl sm:text-3xl tracking-tight">Class of {year}</h1>
        <div className="flex items-center divide-x divide-border rounded-lg border border-border bg-card shadow-sm overflow-hidden">
          {stats.map((st) => {
            const Icon = st.icon;
            return (
              <div key={st.label} data-testid={`class-stat-${st.label.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center gap-2.5 px-4 py-2">
                <div className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${st.accent}`}><Icon className="h-4 w-4" /></div>
                <div className="leading-tight">
                  <div className="text-lg font-serif font-bold">{st.value}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{st.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {data.recent && data.recent.length > 0 && (
        <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden mb-4" data-testid="class-recent">
          <button type="button" data-testid="class-recent-collapse" onClick={() => setRecentOpen(!recentOpen)}
            className="w-full px-4 py-2.5 flex items-center gap-2 border-b border-border hover:text-primary transition-colors">
            {recentOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            <Clock className="h-4 w-4 text-primary" />
            <span className="font-serif text-base tracking-tight">Most Recent Updates</span>
            <span className="text-[11px] text-muted-foreground ml-auto">{ago(data.recent[0].at)}</span>
          </button>
          {recentOpen && (
            <ul className="divide-y divide-border">
              {data.recent.map((r, i) => (
                <li key={i} data-testid={`class-recent-${i}`} className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-muted/50">
                  <button onClick={() => navigate(`/alumni/${r.id}`)} className="font-medium text-primary hover:underline truncate max-w-[180px] text-left shrink-0">{r.name || "—"}</button>
                  <span className="text-muted-foreground truncate flex-1 min-w-0">
                    <span className="font-medium text-foreground/80">{r.action}</span>{r.detail ? ` — ${r.detail}` : ""}
                    {r.by_name ? <span className="text-muted-foreground/70"> · by {r.by_name}</span> : null}
                  </span>
                  <span className="text-muted-foreground/70 shrink-0 text-xs">{ago(r.at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 border-b border-border flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input data-testid="class-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search within this class…" className="w-full pl-10 pr-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Sort</span>
            {[["name", "Name"], ["city", "City"], ["state", "State"]].map(([k, label]) => (
              <button key={k} data-testid={`class-sort-${k}`} onClick={() => toggleSort(k)}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border text-xs font-medium transition-colors ${sort.key === k ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:bg-muted"}`}>
                {label} {sortIcon(k)}
              </button>
            ))}
            <button data-testid="class-hide-deceased"
              onClick={() => setHideDeceased((v) => { const nv = !v; localStorage.setItem("ud_classdetail_hide_deceased", String(nv)); return nv; })}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-medium transition-colors ${hideDeceased ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:bg-muted"}`}>
              {hideDeceased ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} Hide deceased
            </button>
            <button data-testid="class-hide-optout"
              onClick={() => setHideOptedOut((v) => { const nv = !v; localStorage.setItem("ud_classdetail_hide_optout", String(nv)); return nv; })}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-medium transition-colors ${hideOptedOut ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:bg-muted"}`}>
              <MailX className="h-3.5 w-3.5" /> Hide opted out
            </button>
          </div>
        </div>
        <div className="divide-y divide-border">
          {roster.map((a) => (
            <div key={a.id} data-testid={`roster-${a.id}`} onClick={() => navigate(`/alumni/${a.id}`)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/60 transition-colors text-left cursor-pointer">
              {a.photo ? <img src={fileUrl(a.photo)} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" /> : <div className="h-8 w-8 rounded-full bg-secondary/10 flex items-center justify-center text-xs font-semibold text-secondary shrink-0">{(a.name || "?").trim().slice(0, 1)}</div>}
              <span className="font-medium truncate shrink-0 max-w-[200px]">{a.name.trim() || "—"}</span>
              <span className="text-sm text-muted-foreground truncate flex-1 min-w-0">{[a.email, [a.city, a.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ") || "No contact info"}</span>
              {a.solicit && <span data-testid={`roster-solicit-${a.id}`} title="Personal Solicit Code" className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground bg-muted rounded px-1.5 py-0.5 shrink-0 hidden md:inline">{a.solicit}</span>}
              {a.opted_out && <span data-testid={`roster-optout-${a.id}`} title="Opted out of email (self-unsubscribe or solicit code)" className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-800 shrink-0">Opted out</span>}
              {a.is_class_agent && <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-accent/20 text-primary shrink-0 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Agent</span>}
              {a.living_status === "deceased" && <span className="text-[10px] px-2 py-0.5 rounded bg-slate-200 text-slate-700 shrink-0">Deceased</span>}
              {isAdmin && (
                <button
                  data-testid={`class-agent-toggle-${a.id}`}
                  onClick={(e) => toggleAgent(a, e)}
                  title={a.is_class_agent ? "Remove Class Agent" : (a.claimed ? "Make Class Agent" : "Make Class Agent (activates when they claim)")}
                  className={`shrink-0 p-1.5 rounded-md border transition-colors ${a.is_class_agent ? "bg-accent/20 border-accent text-primary" : "border-border text-muted-foreground hover:text-primary hover:border-primary"}`}
                >
                  <ShieldCheck className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {roster.length === 0 && <div className="px-4 py-10 text-center text-muted-foreground">No alumni found.</div>}
        </div>
      </div>
    </div>
  );
}
